/**
 * Utility functions to generate fallback URLs when short.io URL shortening fails.
 * These long-form URLs ensure users can always share deal information with customers,
 * even if the URL shortening service is unavailable.
 */

export interface FallbackUrlParams {
    dealExchangeId: string;
    env: string;
}

export interface FallbackUrls {
    shopperPageUrl: string;
    creditAppUrl: string;
    testDriveDeepLinkUrl: string;
    docUploadDeepLinkUrl: string;
}

/**
 * Extract environment from the current hostname.
 * Used to determine which environment to target for fallback URLs.
 *
 * @returns Environment string: 'dev', 'qa', or '' (empty string for production)
 */
export const getEnvironment = (): string => {
    // Check for PR environment first
    if ((window as any).prEnv) {
        return (window as any).prEnv;
    }

    const hostname = window.location.hostname;

    // Handle localhost and development environments
    if (hostname.includes('localhost')) {
        return 'dev';
    }

    if (hostname.startsWith('dev.')) {
        return 'dev';
    }

    if (hostname.startsWith('qa.')) {
        return 'qa';
    }

    // Production has no env prefix
    return '';
};

/**
 * Build the base domain for shop.dealer.com URLs based on environment.
 *
 * @param env - Environment string ('dev', 'qa', or '')
 * @returns Domain string (e.g., 'dev.shop.dealer.com' or 'shop.dealer.com')
 */
const buildDomain = (env: string): string => {
    if (env === '') {
        return 'shop.dealer.com';
    }
    return `${env}.shop.dealer.com`;
};

/**
 * Build a shopper page URL with the specified route.
 *
 * @param env - Environment string
 * @param dealExchangeId - Deal Exchange ID (will be URL encoded)
 * @param route - Route parameter for the URL
 * @returns Complete URL string
 */
const buildShopperPageUrl = (env: string, dealExchangeId: string, route: string): string => {
    const domain = buildDomain(env);
    const encodedDealId = encodeURIComponent(dealExchangeId);
    return `https://${domain}/shopperPage.html?dsEnv=${env}&dealXgId=${encodedDealId}&route=${route}&isCustomerView=true`;
};

/**
 * Generate all fallback URLs for a deal when short.io URL generation fails.
 *
 * Creates long-form URLs for:
 * - Shopper quote dashboard
 * - Credit application
 * - Test drive scheduling
 * - Document upload
 *
 * @param dealExchangeId - Deal Exchange ID from state
 * @param env - Optional environment override. If not provided, uses getEnvironment()
 * @returns Object containing all four fallback URLs
 */
export const generateFallbackUrls = (dealExchangeId: string, env?: string): FallbackUrls => {
    const environment = env ?? getEnvironment();

    return {
        shopperPageUrl: buildShopperPageUrl(environment, dealExchangeId, 'shopper-quote-dashboard'),
        creditAppUrl: buildShopperPageUrl(environment, dealExchangeId, 'credit'),
        testDriveDeepLinkUrl: buildShopperPageUrl(environment, dealExchangeId, 'testDrive'),
        docUploadDeepLinkUrl: buildShopperPageUrl(environment, dealExchangeId, 'docUpload')
    };
};
